import { useState, useRef, useCallback } from 'react';
import Library from 'services/library';
import useApiCall from 'hooks/useApiCall';
import { useInfiniteScroll as useInfiniteScrollHook } from 'react-infinite-scroll-hook';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { usePreviousSync } from 'hooks/usePrevious';
import isEqual from 'lodash/isEqual';
import catalogCache, { catalogCacheKey } from '../homeStateCache';

const LIMIT = 40;
const emptyArr = [];

export const useInfiniteCatalogScroll = ({ searchValue, sort, filters = emptyArr }) => {
  const cacheKey = catalogCacheKey({ searchValue, sort, filters });
  // Adopt the cached pages when we are coming back to exactly the query the user
  // left behind, otherwise start from scratch. Decided once, on mount.
  const restoredRef = useRef(null);
  if (restoredRef.current === null) restoredRef.current = catalogCache.key === cacheKey && catalogCache.items.length > 0;
  const restored = restoredRef.current;

  const itemsRef = useRef(restored ? catalogCache.items : emptyArr);
  const offsetRef = useRef(restored ? catalogCache.offset : 0);
  const countRef = useRef(restored ? catalogCache.count : 0);
  const [, rerender] = useState(false);

  const resetMemoResultsProps = { searchValue, sort, filters };
  const resetResultsProps = usePreviousSync(resetMemoResultsProps, resetMemoResultsProps);
  const shouldResetResults = !isEqual(resetMemoResultsProps, resetResultsProps);
  if (shouldResetResults) { itemsRef.current = []; offsetRef.current = 0; catalogCache.scrollY = 0; rerender(Math.random()); window.scrollTo(0, 0); }
  const offset = offsetRef.current;

  const { error, data, component, refetch, loading } = useApiCall(() => Library.items({ limit: LIMIT, offset, query: searchValue, sort, filters }), [LIMIT, offset, searchValue, sort, filters]);
  const items = get(data, 'results', emptyArr);
  const count = get(data, 'count', 0);
  if (!loading) itemsRef.current = uniqBy([...itemsRef.current, ...items], 'id');
  if (!loading) countRef.current = count;
  const onLoadMore = useCallback(() => { offsetRef.current += LIMIT; rerender(Math.random()); }, []);
  const hasNextPage = !!(data || {}).next;
  const infiniteRef = useInfiniteScrollHook({ loading, hasNextPage, onLoadMore, scrollContainer: 'window', threshold: 500 /* px */ });

  catalogCache.key = cacheKey;
  catalogCache.items = itemsRef.current;
  catalogCache.offset = offsetRef.current;
  catalogCache.count = countRef.current;

  return { infiniteRef, refetch, error, items: itemsRef.current, count: countRef.current, component, loading };
};
